import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Grid } from '@mui/material';
import { Checkbox, HSpacer, RoutalDialog, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import DialogWarning from '../../../../assets/svg/DialogWarning';
import * as routing from '../../../../constants/Routing';
import ConfirmDialog from '../../../../dialogs/ConfirmDialog';
import { Driver } from '../../../../domain/driver/Driver';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { Project } from '../../../../domain/project/Project';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../../react-components/Text';
import { anyStopHasAnyWarnings } from '../components/OptimizationWarningsTooltip';
import { useOptimizePlan } from '../hooks/plan-details/useOptimizePlan';
import { useRetrieveActivePlanStops } from '../hooks/plan-details/useRetrieveActivePlanStops';

type OptimizationDialogProps = {
  dialogOpen: boolean;
  onClose: () => void;
  activePlan?: PlanFlat;
  activeProject?: Project;
  drivers?: Driver[];
};

export const OptimizationDialog = ({
  dialogOpen,
  onClose,
  drivers,
  activePlan,
  activeProject,
}: OptimizationDialogProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { data: stops } = useRetrieveActivePlanStops();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();
  const { run: optimizePlan } = useOptimizePlan();
  const { data: userCredentials } = useRetrieveUserCredentials();

  const [keepCurrentAssignment, setKeepCurrentAssignment] = useState<boolean>(true);

  const [dialog, setDialog] = useState<`optimize` | `warnings` | undefined>(undefined);

  useEffect(() => {
    if (dialogOpen && !dialog) {
      setDialog(`optimize`);
    }
  }, [dialogOpen, dialog]);

  useEffect(() => {
    if (!dialog) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog]);

  const live = drivers?.some((driver: any) => driver.status !== `not_started`);

  const explorerDate = moment.tz(moment.tz.guess(true));
  const projectDate = moment.tz(activeProject?.time_zone ?? ``);
  const isTimeDifferent =
    explorerDate.hours() !== projectDate.hours() || explorerDate.minutes() !== projectDate.minutes();

  const handleOptimizePlan = (keepCurrent?: boolean) => {
    if (activePlan) {
      optimizePlan({
        plan: activePlan,
        keepCurrentAssignment: keepCurrent,
      });
    }
  };

  return (
    <>
      <RoutalDialog
        open={Boolean(dialog === `optimize` && dialogOpen)}
        titleContent={<Text variant="h6" intlId="planner.plan_details.header.confirm_optimize_dialog.title" />}
        cancelButtonText={intl.formatMessage({ id: `cancel` })}
        confirmButtonText={intl.formatMessage({ id: `confirm` })}
        onCloseDialog={() => setDialog(undefined)}
        onConfirm={() => {
          if (
            anyStopHasAnyWarnings({
              stops: stops ?? [],
              drivers: drivers ?? [],
              planGeoFences: projectGeoFences ?? [],
              userCredentials,
            })
          ) {
            setDialog(`warnings`);
          } else {
            handleOptimizePlan(keepCurrentAssignment);
            setDialog(undefined);
          }
        }}
        contentStyles={{ padding: `40px 20px 40px 20px` }}
      >
        <Grid container direction="column" wrap="nowrap">
          <Grid
            item
            container
            direction="column"
            wrap="nowrap"
            alignItems="center"
            justifyContent="space-between"
            style={
              {
                // padding: `20px`,
                // borderRadius: `10px`,
                // backgroundColor: RoutalPalette.warning.light,
              }
            }
          >
            <Grid item container direction="column" wrap="nowrap" alignItems="center" justifyContent="space-between">
              {live && (
                <Text
                  variant="body-large"
                  intlId="planner.plan_details.header.confirm_optimize_dialog.content_live"
                  style={{ paddingBottom: `24px`, color: RoutalPalette.primary80 }}
                />
              )}
              <Grid
                item
                container
                direction="row"
                wrap="nowrap"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  padding: `20px`,
                  borderRadius: `10px`,
                  backgroundColor: RoutalPalette.warning.light,
                }}
              >
                <Grid item container direction="row" wrap="nowrap" alignItems="center" justifyContent="space-between">
                  <Checkbox
                    checked={keepCurrentAssignment}
                    onChange={(e: any, value: any) => {
                      setKeepCurrentAssignment(!keepCurrentAssignment);
                    }}
                  />
                  <Grid item container direction="row" wrap="nowrap" alignItems="center">
                    <HSpacer small />
                    <Text intlId="planner.plan_details.header.assignment_optimize_dialog.checkbox" style={{}} />
                  </Grid>
                </Grid>

                <Grid
                  item
                  container
                  direction="row"
                  wrap="nowrap"
                  alignItems="center"
                  style={{
                    width: `unset`,
                  }}
                >
                  <DialogWarning style={{ color: RoutalPalette.primary40 }} />
                </Grid>
              </Grid>
            </Grid>
            {isTimeDifferent && live && activeProject && (
              <Grid
                style={{
                  marginTop: `24px`,
                  padding: `14px`,
                  border: `1px solid ${RoutalPalette.neutral10}`,
                  borderRadius: `10px`,
                }}
              >
                <Text
                  variant="body"
                  intlId="planner.plan_details.header.confirm_optimize_dialog.warning_timezones"
                  style={{ color: RoutalPalette.secondary00 }}
                />
                <VSpacer small />
                <Grid
                  style={{
                    display: `flex`,
                    justifyContent: `space-around`,
                    flexWrap: `nowrap`,
                    flexDirection: `row`,
                    alignItems: `center`,
                    width: `100%`,
                  }}
                >
                  <Grid
                    style={{
                      display: `flex`,
                      justifyContent: `center`,
                      flexWrap: `nowrap`,
                      flexDirection: `column`,
                      alignItems: `center`,
                    }}
                  >
                    <Text
                      variant="body"
                      intlId="Web Browser Time"
                      style={{ paddingTop: `10px`, color: RoutalPalette.secondary20 }}
                    />
                    {explorerDate.hours() > 9 ? explorerDate.hours() : `0` + explorerDate.hours()}:
                    {explorerDate.minutes() > 9 ? explorerDate.minutes() : `0` + explorerDate.minutes()}
                  </Grid>
                  <Grid
                    style={{
                      display: `flex`,
                      justifyContent: `center`,
                      flexWrap: `nowrap`,
                      flexDirection: `column`,
                      alignItems: `center`,
                    }}
                  >
                    <Text
                      variant="body"
                      intlId="Project Time"
                      style={{ paddingTop: `10px`, color: RoutalPalette.secondary20 }}
                    />
                    {projectDate.hours() > 9 ? projectDate.hours() : `0` + projectDate.hours()}:
                    {projectDate.minutes() > 9 ? projectDate.minutes() : `0` + projectDate.minutes()}
                  </Grid>
                </Grid>
                <VSpacer large />
                <Grid style={{ display: `flex`, alignItems: `center` }}>
                  <ArrowForwardRoundedIcon style={{ color: RoutalPalette.primary40 }} />
                  <Text
                    tagName="span"
                    variant="button"
                    onClick={() => {
                      navigate(`${routing.PROJECT_SETTINGS.GENERAL.replace(`:projectId`, activeProject.id)}`);
                    }}
                    intlId="planner.plan_details.header.confirm_optimize_dialog.go_to_settings"
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </RoutalDialog>

      <ConfirmDialog
        title={<Text variant="h5" intlId="planner.plan_details.header.optimization_warnings_dialog.title" />}
        description={<Text variant="body" intlId="planner.plan_details.header.optimization_warnings_dialog.content" />}
        open={Boolean(dialog === `warnings` && dialogOpen)}
        onCloseDialog={() => setDialog(undefined)}
        onConfirm={() => {
          handleOptimizePlan(keepCurrentAssignment);
          setDialog(undefined);
        }}
      />
    </>
  );
};
